import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Megaphone, Pin, X, Save } from "lucide-react";
import { opsAPI, PRIORITY_STYLE, timeAgo, cls } from "./opsApi";

export default function OpsAnnouncements() {
  const [rows, setRows] = useState([]);
  const [creating, setCreating] = useState(false);

  const load = () => opsAPI.announcements({ active_only: false }).then(setRows);
  useEffect(() => { load(); }, []);

  const deactivate = async (id) => {
    if (!window.confirm("Retire this announcement?")) return;
    try { await opsAPI.deactivateAnn(id); load(); }
    catch { toast.error("Failed"); }
  };

  return (
    <div className="space-y-4" data-testid="ops-announcements">
      <div className="flex items-center gap-3">
        <div className="text-[13px] text-slate-500">
          Hotel-wide announcements — VIP arrivals, group check-ins, pool closures, emergency notices. Target the whole hotel or specific departments/roles.
        </div>
        <button onClick={()=>setCreating(true)} data-testid="ops-ann-new"
                className="ml-auto h-10 px-4 rounded-lg bg-hocp-gold text-hocp-eerie text-[13px] font-semibold inline-flex items-center gap-2 hover:brightness-95">
          <Plus className="w-4 h-4"/> New Announcement
        </button>
      </div>

      {rows.length === 0
        ? <div className="p-12 bg-white border border-slate-200 rounded-2xl text-center">
            <Megaphone className="w-12 h-12 mx-auto text-slate-300 mb-2"/>
            <div className="text-[13px] text-slate-500">No announcements posted.</div>
          </div>
        : (
          <div className="space-y-3">
            {rows.map(a => (
              <div key={a.id} data-testid={`ops-ann-${a.id}`}
                   className={`bg-white border rounded-2xl p-4 ${a.is_active === false ? "opacity-50 border-slate-200" : "border-slate-200"} ${a.pinned ? "ring-2 ring-hocp-gold/40" : ""}`}>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-hocp-navy text-white flex items-center justify-center shrink-0">
                    <Megaphone className="w-4 h-4"/>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-display text-[15px] font-semibold text-hocp-navy">{a.title}</div>
                      <span className={`px-2 py-0.5 rounded-md text-[10.5px] font-medium capitalize ${PRIORITY_STYLE[a.priority]}`}>{a.priority}</span>
                      {a.pinned && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-hocp-gold/20 text-hocp-navy text-[10.5px] font-medium">
                        <Pin className="w-3 h-3"/> Pinned
                      </span>}
                      {a.is_active === false && <span className="px-2 py-0.5 rounded-md bg-slate-100 text-slate-500 text-[10.5px] font-medium">Inactive</span>}
                    </div>
                    <div className="text-[13px] text-slate-700 mt-1 whitespace-pre-wrap">{a.body}</div>
                    <div className="text-[11px] mono text-slate-400 mt-2">
                      by {a.created_by_email} · {timeAgo(a.created_at)}
                    </div>
                  </div>
                  {a.is_active !== false && (
                    <button onClick={()=>deactivate(a.id)} data-testid={`ops-ann-retire-${a.id}`}
                            className="h-8 px-3 rounded-lg text-rose-600 hover:bg-rose-50 text-[12px]">Retire</button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

      {creating && <CreateAnn onClose={()=>setCreating(false)} onDone={()=>{ setCreating(false); load(); }}/>}
    </div>
  );
}

function CreateAnn({ onClose, onDone }) {
  const [f, setF] = useState({ title:"", body:"", priority:"normal", pinned: false, audience: { all: true } });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!f.title.trim() || !f.body.trim()) { toast.error("Title and body required"); return; }
    setSaving(true);
    try { await opsAPI.createAnn(f); toast.success("Posted"); onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg" onClick={e=>e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-display text-[16px] font-semibold text-hocp-navy">New Announcement</h3>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100"><X className="w-4 h-4 text-slate-500"/></button>
        </div>
        <div className="p-5 space-y-3">
          <label className="block"><span className="block text-[11.5px] font-medium text-slate-600 mb-1">Title</span>
            <input value={f.title} onChange={e=>setF({...f, title: e.target.value})} placeholder="e.g. New parking policy"
                   data-testid="ops-ann-title" className={cls + " w-full"}/></label>
          <label className="block"><span className="block text-[11.5px] font-medium text-slate-600 mb-1">Body</span>
            <textarea value={f.body} onChange={e=>setF({...f, body: e.target.value})} rows={4}
                       placeholder="Body of the announcement" data-testid="ops-ann-body"
                       className={cls + " w-full h-auto py-2 resize-y"}/></label>
          <div className="grid grid-cols-2 gap-2">
            <select value={f.priority} onChange={e=>setF({...f, priority: e.target.value})}
                    data-testid="ops-ann-priority" className={cls}>
              {["low","normal","high","critical"].map(p => <option key={p} value={p}>{p}</option>)}
            </select>
            <label className="inline-flex items-center gap-2 text-[13px] px-3 h-10 border border-slate-200 rounded-lg bg-white cursor-pointer">
              <input type="checkbox" checked={f.pinned}
                     onChange={e=>setF({...f, pinned: e.target.checked})}
                     data-testid="ops-ann-pinned"/> Pin to top
            </label>
          </div>
        </div>
        <div className="px-5 py-3 bg-slate-50 flex justify-end gap-2 border-t border-slate-100">
          <button onClick={onClose} className="h-9 px-3.5 rounded-lg text-slate-600 text-[13px] hover:bg-slate-100">Cancel</button>
          <button onClick={submit} disabled={saving} data-testid="ops-ann-submit"
                  className="h-9 px-4 rounded-lg bg-hocp-gold text-hocp-eerie text-[13px] font-medium inline-flex items-center gap-2 disabled:opacity-60">
            <Save className="w-4 h-4"/> {saving ? "Posting…" : "Post Announcement"}
          </button>
        </div>
      </div>
    </div>
  );
}
